<template>
    <div class="home-animation" :class="stage.classes" :style="{ '--seek': stage.seek }" aria-hidden="true">
        <svg :key="phase" class="home-animation-svg" viewBox="-40 0 392 112" fill="none" aria-hidden="true">
            <g class="lo-list">
                <g class="lo-mark" transform="translate(26,92)" style="--mi: 0.05s; --rd: 0s; --fd: 6.4s; --bd: 0.15s; --bx: 38px; --by: -56px">
                    <g class="lo-bullet">
                        <g class="lo-spin">
                            <g class="lo-lift"><path class="lo-sq" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--tonal-teal-strong)" style="--sq: 2.6s" /></g>
                            <g class="lo-roll">
                                <g class="lo-fold">
                                    <g class="lo-drop"><path class="lo-sq" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--tonal-teal-base)" style="--sq: 3.2s" /></g>
                                </g>
                            </g>
                        </g>
                    </g>
                </g>
                <g class="lo-mark" transform="translate(130,92)" style="--mi: 0.19s; --rd: 0.22s; --fd: 6.56s; --bd: 0.33s; --bx: -66px; --by: -20px">
                    <g class="lo-bullet">
                        <g class="lo-spin">
                            <g class="lo-lift"><path class="lo-sq" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--tonal-teal-strong)" style="--sq: 3.8s" /></g>
                            <g class="lo-roll">
                                <g class="lo-fold">
                                    <g class="lo-drop"><path class="lo-sq" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--tonal-teal-base)" style="--sq: 4.4s" /></g>
                                </g>
                            </g>
                        </g>
                    </g>
                </g>
                <g class="lo-mark" transform="translate(234,92)" style="--mi: 0.33s; --rd: 0.44s; --fd: 6.72s; --bd: 0.51s; --bx: -170px; --by: 16px">
                    <g class="lo-bullet">
                        <g class="lo-spin">
                            <g class="lo-lift"><path class="lo-sq" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--tonal-teal-strong)" style="--sq: 5s" /></g>
                            <g class="lo-roll">
                                <g class="lo-fold">
                                    <g class="lo-drop"><path class="lo-sq" d="M-26 0A26 26 0 0 1 26 0Z" fill="var(--tonal-teal-base)" style="--sq: 5.6s" /></g>
                                </g>
                            </g>
                        </g>
                    </g>
                </g>
                <g transform="translate(0,92)">
                    <g class="lo-ballx">
                        <g class="lo-bally"><circle class="lo-ball" r="9" /></g>
                    </g>
                </g>
                <rect class="lo-line" x="86" y="26" width="164" height="8" rx="4" fill="var(--color-border-strong)" style="--ld: 1s" />
                <rect class="lo-line" x="86" y="62" width="140" height="8" rx="4" fill="var(--color-border-strong)" style="--ld: 1.18s" />
                <rect class="lo-line" x="86" y="98" width="152" height="8" rx="4" fill="var(--color-border-strong)" style="--ld: 1.36s" />
            </g>
        </svg>
    </div>
</template>

<script setup lang="ts">
/* eslint design-system/no-off-scale-spacing: "off" -- Preserves the existing hourglass artwork and the reference's authored animation geometry. */
import { usePreferredReducedMotion, useTimeoutFn } from "@vueuse/core";
import { computed, onMounted, ref, watch } from "vue";

const reducedMotion = usePreferredReducedMotion();
const phase = ref(0);
// The handoff loops the original hourglass scene, independently of action generation.
// Finish the rocking cycle so its final position matches the start of the fall.
const stages = [
    { classes: "is-idle", seek: "0s", duration: 3600 },
    { classes: "is-running", seek: "0s", duration: 7800 },
    { classes: "is-running is-spin", seek: "7.8s", duration: 3200 },
    { classes: "is-running is-run2", seek: "7.8s", duration: 6400 },
];
const stage = computed(() => stages[phase.value]);
const { start, stop } = useTimeoutFn(
    () => {
        phase.value = (phase.value + 1) % stages.length;
        start();
    },
    computed(() => stage.value.duration),
    { immediate: false },
);

function restart() {
    stop();
    phase.value = 0;
    if (reducedMotion.value !== "reduce") {
        start();
    }
}
onMounted(restart);
watch(reducedMotion, restart);
</script>

<style scoped>
@layer components {
    .home-animation {
        --seek: 0s;
        --seek2: 0s;
        width: 100%;
        min-width: 0;
        display: flex;
        justify-content: center;
        overflow: hidden;
    }
    .home-animation-svg {
        width: 100%;
        max-width: 300px;
        height: auto;
        display: block;
        overflow: hidden;
    }

    .lo-roll {
        transform: translate(0, -52px) rotate(180deg);
    }
    .lo-ball {
        opacity: 0;
    }

    .is-idle .lo-mark {
        animation: lo-mark-in 0.45s ease both var(--mi);
    }
    .is-idle .lo-roll {
        animation: lo-rock 3.6s linear infinite calc(0s - var(--rockseek, 0s));
    }

    .is-running .lo-roll {
        animation: lo-fall 0.95s linear both calc(var(--rd) - var(--seek));
    }
    .is-running .lo-drop {
        animation: lo-land 0.44s ease-out both calc(var(--rd) + 0.95s - var(--seek));
    }
    .is-running .lo-sq {
        animation: lo-squash 0.34s ease-out both calc(var(--sq) - var(--seek));
    }
    .is-running .lo-lift {
        animation: lo-lift 0.75s cubic-bezier(0.22, 1, 0.36, 1) both calc(var(--fd) - var(--seek));
    }
    .is-running .lo-fold {
        animation: lo-fold 0.95s cubic-bezier(0.34, 0.9, 0.3, 1) both calc(var(--fd) + 0.02s - var(--seek));
    }
    .is-bounce .lo-lift,
    .is-bounce .lo-fold {
        animation: none;
    }
    .is-spin .lo-spin {
        animation: lo-spin 1.6s cubic-bezier(0.65, 0, 0.35, 1) infinite calc(0s - var(--spinseek, 0s));
    }

    .is-run2 .lo-bullet {
        animation: lo-bullet 0.85s cubic-bezier(0.5, 0, 0.2, 1) both calc(var(--bd) - var(--seek2));
    }
    .is-run2 .lo-line {
        animation:
            lo-wipe 0.55s cubic-bezier(0.22, 1, 0.36, 1) both calc(var(--ld) - var(--seek2)),
            lo-skeleton 1.4s ease-in-out 2 both calc(var(--ld) + 0.55s - var(--seek2));
    }
    .is-run2 .lo-list {
        animation: lo-ready 0.7s cubic-bezier(0.22, 1, 0.36, 1) both calc(4.9s - var(--seek2));
    }
    .is-running .lo-ballx {
        animation: lo-ballx 4.2s linear both calc(2s - var(--seek));
    }
    .is-running .lo-bally {
        animation: lo-bally 0.3s cubic-bezier(0.2, 0.66, 0.4, 1) 14 alternate both calc(2s - var(--seek));
    }
    .is-running .lo-ball {
        animation:
            lo-ball-show 4.2s linear both calc(2s - var(--seek)),
            lo-ball-tint 4.2s step-end both calc(2s - var(--seek));
    }

    @keyframes lo-mark-in {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    @keyframes lo-rock {
        0% {
            transform: translate(-14.33px, -49.99px) rotate(148deg);
        }
        4.17% {
            transform: translate(-13.86px, -50.12px) rotate(149.09deg);
        }
        8.33% {
            transform: translate(-12.45px, -50.49px) rotate(152.29deg);
        }
        12.50% {
            transform: translate(-10.2px, -50.99px) rotate(157.37deg);
        }
        16.67% {
            transform: translate(-7.24px, -51.49px) rotate(164deg);
        }
        20.83% {
            transform: translate(-3.76px, -51.86px) rotate(171.72deg);
        }
        25% {
            transform: translate(-0px, -52px) rotate(180deg);
        }
        29.17% {
            transform: translate(3.76px, -51.86px) rotate(188.28deg);
        }
        33.33% {
            transform: translate(7.24px, -51.49px) rotate(196deg);
        }
        37.50% {
            transform: translate(10.2px, -50.99px) rotate(202.63deg);
        }
        41.67% {
            transform: translate(12.45px, -50.49px) rotate(207.71deg);
        }
        45.83% {
            transform: translate(13.86px, -50.12px) rotate(210.91deg);
        }
        50% {
            transform: translate(14.33px, -49.99px) rotate(212deg);
        }
        54.17% {
            transform: translate(13.86px, -50.12px) rotate(210.91deg);
        }
        58.33% {
            transform: translate(12.45px, -50.49px) rotate(207.71deg);
        }
        62.50% {
            transform: translate(10.2px, -50.99px) rotate(202.63deg);
        }
        66.67% {
            transform: translate(7.24px, -51.49px) rotate(196deg);
        }
        70.83% {
            transform: translate(3.76px, -51.86px) rotate(188.28deg);
        }
        75% {
            transform: translate(0px, -52px) rotate(180deg);
        }
        79.17% {
            transform: translate(-3.76px, -51.86px) rotate(171.72deg);
        }
        83.33% {
            transform: translate(-7.24px, -51.49px) rotate(164deg);
        }
        87.50% {
            transform: translate(-10.2px, -50.99px) rotate(157.37deg);
        }
        91.67% {
            transform: translate(-12.45px, -50.49px) rotate(152.29deg);
        }
        95.83% {
            transform: translate(-13.86px, -50.12px) rotate(149.09deg);
        }
        100% {
            transform: translate(-14.33px, -49.99px) rotate(148deg);
        }
    }

    @keyframes lo-fall {
        0% {
            transform: translate(-14.33px, -49.99px) rotate(148deg);
        }
        2.50% {
            transform: translate(-14.28px, -50px) rotate(148.13deg);
        }
        5% {
            transform: translate(-14.1px, -50.05px) rotate(148.53deg);
        }
        7.50% {
            transform: translate(-13.81px, -50.13px) rotate(149.19deg);
        }
        10% {
            transform: translate(-13.41px, -50.24px) rotate(150.12deg);
        }
        12.50% {
            transform: translate(-12.88px, -50.38px) rotate(151.31deg);
        }
        15% {
            transform: translate(-12.24px, -50.54px) rotate(152.77deg);
        }
        17.50% {
            transform: translate(-11.48px, -50.72px) rotate(154.49deg);
        }
        20% {
            transform: translate(-10.6px, -50.91px) rotate(156.48deg);
        }
        22.50% {
            transform: translate(-9.6px, -51.11px) rotate(158.73deg);
        }
        25% {
            transform: translate(-8.47px, -51.31px) rotate(161.25deg);
        }
        27.50% {
            transform: translate(-7.22px, -51.5px) rotate(164.03deg);
        }
        30% {
            transform: translate(-5.85px, -51.67px) rotate(167.08deg);
        }
        32.50% {
            transform: translate(-4.35px, -51.82px) rotate(170.39deg);
        }
        35% {
            transform: translate(-2.74px, -51.93px) rotate(173.97deg);
        }
        37.50% {
            transform: translate(-0.99px, -51.99px) rotate(177.81deg);
        }
        40% {
            transform: translate(0.87px, -51.99px) rotate(181.92deg);
        }
        42.50% {
            transform: translate(2.85px, -51.92px) rotate(186.29deg);
        }
        45% {
            transform: translate(4.95px, -51.76px) rotate(190.93deg);
        }
        47.50% {
            transform: translate(7.16px, -51.5px) rotate(195.83deg);
        }
        50% {
            transform: translate(9.48px, -51.13px) rotate(201deg);
        }
        52.50% {
            transform: translate(11.89px, -50.62px) rotate(206.43deg);
        }
        55% {
            transform: translate(14.39px, -49.97px) rotate(212.13deg);
        }
        57.50% {
            transform: translate(16.97px, -49.15px) rotate(218.09deg);
        }
        60% {
            transform: translate(19.61px, -48.16px) rotate(224.32deg);
        }
        62.50% {
            transform: translate(22.31px, -46.97px) rotate(230.81deg);
        }
        65% {
            transform: translate(25.04px, -45.57px) rotate(237.57deg);
        }
        67.50% {
            transform: translate(27.78px, -43.96px) rotate(244.59deg);
        }
        70% {
            transform: translate(30.52px, -42.1px) rotate(251.88deg);
        }
        72.50% {
            transform: translate(33.23px, -40px) rotate(259.43deg);
        }
        75% {
            transform: translate(35.88px, -37.64px) rotate(267.25deg);
        }
        77.50% {
            transform: translate(38.44px, -35.02px) rotate(275.33deg);
        }
        80% {
            transform: translate(40.89px, -32.13px) rotate(283.68deg);
        }
        82.50% {
            transform: translate(43.18px, -28.97px) rotate(292.29deg);
        }
        85% {
            transform: translate(45.3px, -25.54px) rotate(301.17deg);
        }
        87.50% {
            transform: translate(47.19px, -21.85px) rotate(310.31deg);
        }
        90% {
            transform: translate(48.82px, -17.9px) rotate(319.72deg);
        }
        92.50% {
            transform: translate(50.16px, -13.72px) rotate(329.39deg);
        }
        95% {
            transform: translate(51.16px, -9.33px) rotate(339.33deg);
        }
        97.50% {
            transform: translate(51.78px, -4.74px) rotate(349.53deg);
        }
        100% {
            transform: translate(52px, -0px) rotate(360deg);
        }
    }
    .lo-drop {
        transform-box: fill-box;
        transform-origin: bottom center;
    }
    @keyframes lo-land {
        0% {
            transform: scale(1, 1);
        }
        20% {
            transform: scale(1.12, 0.85);
        }
        52% {
            transform: scale(0.96, 1.05);
        }
        78% {
            transform: scale(1.02, 0.98);
        }
        100% {
            transform: scale(1, 1);
        }
    }
    @keyframes lo-ballx {
        from {
            transform: translateX(-26px);
        }
        to {
            transform: translateX(338px);
        }
    }
    @keyframes lo-bally {
        from {
            transform: translateY(-35px);
        }
        to {
            transform: translateY(-61px);
        }
    }
    @keyframes lo-ball-show {
        0% {
            opacity: 0;
        }
        2% {
            opacity: 1;
        }
        96% {
            opacity: 1;
        }
        100% {
            opacity: 0;
        }
    }
    @keyframes lo-ball-tint {
        0% {
            fill: var(--color-ink-muted);
        }
        14.29% {
            fill: var(--tonal-teal-strong);
        }
        28.57% {
            fill: var(--tonal-teal-base);
        }
        42.86% {
            fill: var(--tonal-teal-strong);
        }
        57.14% {
            fill: var(--tonal-teal-base);
        }
        71.43% {
            fill: var(--tonal-teal-strong);
        }
        85.71% {
            fill: var(--tonal-teal-base);
        }
    }
    .lo-sq {
        transform-box: fill-box;
        transform-origin: bottom center;
    }
    @keyframes lo-squash {
        0% {
            transform: scale(1, 1);
        }
        28% {
            transform: scale(1.1, 0.84);
        }
        100% {
            transform: scale(1, 1);
        }
    }
    @keyframes lo-lift {
        0% {
            transform: translate(0, 0);
        }
        15% {
            transform: translate(2px, 3px);
        }
        100% {
            transform: translate(26px, -26px);
        }
    }
    @keyframes lo-fold {
        0% {
            transform: translate(0, 0) rotate(0deg);
        }
        55% {
            transform: translate(-10px, -54px) rotate(104deg);
        }
        100% {
            transform: translate(-26px, -26px) rotate(180deg);
        }
    }
    @keyframes lo-spin {
        from {
            transform: translate(26px, -26px) rotate(0deg) translate(-26px, 26px);
        }
        to {
            transform: translate(26px, -26px) rotate(360deg) translate(-26px, 26px);
        }
    }
    @keyframes lo-bullet {
        from {
            transform: translate(0, 0) scale(1);
        }
        to {
            transform: translate(var(--bx), var(--by)) scale(0.2308);
        }
    }
    .lo-line {
        transform-box: fill-box;
        transform-origin: left center;
        transform: scaleX(0);
    }
    @keyframes lo-wipe {
        from {
            transform: scaleX(0);
        }
        to {
            transform: scaleX(1);
        }
    }
    @keyframes lo-skeleton {
        0%,
        100% {
            opacity: 1;
        }
        50% {
            opacity: 0.4;
        }
    }
    @keyframes lo-ready {
        from {
            transform: translateY(0);
        }
        to {
            transform: translateY(-4px);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .home-animation *,
        .home-animation *::before,
        .home-animation *::after {
            animation: none;
        }
    }
}
</style>
